/**
 * Resolving the one setting every client needs: where the API is.
 *
 * A base URL looks trivial and is the single most common source of "it works
 * on my machine". The failures are all quiet ones — a build that ships
 * pointing at `localhost`, a phone that cannot reach the Mac's `localhost`
 * because on the phone that *is* the phone, a URL missing `/api/v1` that turns
 * every call into a 404, a trailing slash that makes every path `//vehicles`.
 * None of those announce themselves; they surface as "the app is broken".
 *
 * So this resolves the value in one place, for every app, and refuses anything
 * it cannot vouch for. An app passes the value its bundler inlined and gets
 * back a URL that is known to be absolute, `http(s)`, and free of a trailing
 * slash — or an error naming the variable and what to put in it.
 */
/** Thrown when the API base URL is missing or unusable. */
export class ApiConfigurationError extends Error {
    variable;
    constructor(variable, problem) {
        super(`${problem}\n\n` +
            `Set ${variable} to the versioned API base URL, including /api/v1:\n` +
            `  production   ${PRODUCTION_API_BASE_URL}\n` +
            `  local        ${LOCAL_API_BASE_URL}\n\n` +
            `Copy the app's .env.example to .env and edit it there. Never commit .env.`);
        this.name = 'ApiConfigurationError';
        this.variable = variable;
    }
}
/** The deployed API. */
export const PRODUCTION_API_BASE_URL = 'https://api.carcarna.com/api/v1';
/** A backend started with `php artisan serve` on the same machine. */
export const LOCAL_API_BASE_URL = 'http://localhost:8000/api/v1';
/**
 * Validates and normalises the API base URL for one app.
 *
 * @throws {ApiConfigurationError} when the value is absent with no fallback,
 *   is not an absolute `http(s)` URL, or carries a query or fragment.
 */
export function resolveApiBaseUrl({ variable, configured, fallback, }) {
    const value = configured === undefined || configured.trim() === '' ? fallback : configured.trim();
    if (value === undefined || value === '') {
        throw new ApiConfigurationError(variable, `${variable} is not set.`);
    }
    // Checked before parsing, because the WHATWG parser is forgiving in exactly
    // the way a typo needs it not to be: `http:/api/v1` becomes host `api`, and
    // `http:api.carcarna.com/api/v1` is accepted as-is. Both parse, neither throws, and
    // the app then talks to somewhere nobody intended.
    if (!/^https?:\/\//.test(value)) {
        throw new ApiConfigurationError(variable, `${variable} is "${value}", which does not begin with http:// or https://. ` +
            `A host on its own, a path fragment, or a scheme missing a slash will not do.`);
    }
    let url;
    try {
        url = new URL(value);
    }
    catch {
        throw new ApiConfigurationError(variable, `${variable} is "${value}", which is not an absolute URL. ` +
            `A host on its own or a path fragment will not do — the scheme is required.`);
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        throw new ApiConfigurationError(variable, `${variable} uses the "${url.protocol}" scheme. Only http and https are supported.`);
    }
    if (url.search !== '' || url.hash !== '') {
        // Every request appends a path and its own query. A base that already
        // carries one would produce a URL that silently drops it.
        throw new ApiConfigurationError(variable, `${variable} is "${value}", which carries a query string or fragment. ` +
            `The base URL must be the origin and path only.`);
    }
    // Every caller joins with a leading-slash path, so a trailing slash here is
    // what produces `//vehicles`. Strip it once, here, rather than in each call.
    return `${url.origin}${url.pathname}`.replace(/\/+$/, '');
}
/**
 * Whether a base URL looks like it addresses the versioned API.
 *
 * Deliberately advisory: a proxy or a preview deployment may legitimately
 * serve v1 from another path, so this reports rather than refuses. Callers use
 * it to warn during development, where pointing at `https://api.carcarna.com` and
 * getting the landing page instead of the API is an easy mistake to make and a
 * confusing one to debug.
 */
export function looksVersioned(baseUrl) {
    return /\/v\d+$/.test(baseUrl);
}
//# sourceMappingURL=config.js.map